// src/components/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Heart } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

// --- Configuration Data ---
const QUICK_LINKS = [
  { name: "About Us", to: "/about" },
  { name: "Courses", to: "/courses" },
  { name: "Success Stories", to: "/success-stories" },
  { name: "Book Counselling", to: "/book-free-counselling" },
  { name: "Contact", to: "/contact" }
];

const CONTACT_INFO = {
  phone: "+917305959496",
  phoneLink: "tel:+917305959496",
  email: "manager.6thsense@gmail.com",
  emailLink: "mailto:manager.6thsense@gmail.com",
  address: "Trichy, TamilNadu, India"
};

const SOCIALS = [
  { 
    icon: <Instagram size={18} />, 
    href: "https://www.instagram.com/6thsense_institution?igsh=MW9kNzNwMms4ZGd4", 
    label: "Instagram" 
  },
  { 
    icon: <FaWhatsapp size={18} />, 
    href: "https://wa.me/917305959496?text=Hi, Can I get more info on your courses??", 
    label: "WhatsApp" 
  }
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 text-slate-400 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16 text-sm">

          {/* Column 1: Brand & Bio */}
          <div className="space-y-5">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="/logo.jpg" 
                alt="6th Sense Academy Logo" 
                className="h-9 w-auto rounded-sm" // Added rounded-sm for better polish if logo is square
              />
              <span className="font-bold text-white text-lg group-hover:text-red-500 transition-colors">
                6th Sense
              </span>
            </Link>
            <p className="text-slate-500 max-w-xs leading-relaxed">
              Expert IELTS training & trusted study abroad guidance.
            </p>
            <div className="flex gap-4">
              {SOCIALS.map((social, index) => (
                <a 
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="text-slate-500 hover:text-red-500 transition-colors hover:scale-110 transform duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="text-white font-semibold mb-4 text-base">Quick Links</h3>
            <nav className="flex flex-col space-y-2">
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="w-fit hover:text-white hover:translate-x-1 transition-all duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 3: Contact Info */}
          <div className="space-y-5">
            <h3 className="text-white font-semibold mb-4 text-base">Get in Touch</h3>
            <div className="space-y-4 text-sm">
              <a href={CONTACT_INFO.phoneLink} className="flex items-center gap-3 hover:text-white transition group">
                <Phone size={16} className="text-red-500 group-hover:text-red-400" />
                {CONTACT_INFO.phone}
              </a>
              <a href={CONTACT_INFO.emailLink} className="flex items-center gap-3 hover:text-white transition group">
                <Mail size={16} className="text-red-500 group-hover:text-red-400" />
                {CONTACT_INFO.email}
              </a>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
                <span className="leading-tight">{CONTACT_INFO.address}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          
          <p>© {currentYear} 6th Sense Training Academy. All rights reserved.</p>

          <a 
            href="https://spearheads.pro" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 hover:text-white transition-colors group"
          >
            Made with 
            <Heart 
              size={12} 
              className="text-red-500 fill-red-500 group-hover:scale-110 transition-transform" 
            /> 
            by SpearHeads
          </a>

          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          
        </div>
      </div>
    </footer>
  );
};

export default Footer;